import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import AuthManager from 'components/auth/AuthManager';
import AccountContext from 'context/AccountContext';
import { useContext, useState } from 'react';
import {
  onboardingCompleted,
  afterAuthNavigationStack as setNavigationStack,
} from 'states/persistInStorage';
import { useUiStore } from 'stores/uiStore';
import {
  AccountScreenNavigationProp,
  NestedTabNavProps,
  RootStackParamList,
} from 'types/navigation';
import { checkNotificationsPermissions, isWeb } from 'utils/deviceUtils';

export type AfterAuthNavigationTarget = {
  target:
    | 'BuyShortTermParking'
    | 'BuyPermit'
    | 'Onboarding'
    | 'Account'
    | 'Services'
    | 'MainPage';
  parkingZoneUid?: string;
  parkingSpaceUid?: string;
  pmcId?: string;
};

export type AfterAuthNavigationStack = AfterAuthNavigationTarget[];

const useAfterAuthenticationRedirect = () => {
  const [redirectPushCache, setRedirectPushCache] = useState<
    AfterAuthNavigationTarget[]
  >([]);
  const navigationStack = useReactiveVar(setNavigationStack);
  const isOnboardingCompleted = useReactiveVar(onboardingCompleted);

  const {
    customer: { paymentCards },
  } = useContext(AccountContext);

  const { navigate: navigateAccount } =
    useNavigation<AccountScreenNavigationProp>();
  const { navigate: navigateMain } = useNavigation<NestedTabNavProps>();
  const { navigate: navigateRoot, getState } =
    useNavigation<StackNavigationProp<RootStackParamList>>();
  const pushStack = (
    target: AfterAuthNavigationTarget,
    commitImmediately = true
  ) => {
    const newStack = commitImmediately ? navigationStack : redirectPushCache;
    newStack.push(target);
    commitImmediately
      ? setNavigationStack(newStack)
      : setRedirectPushCache(newStack);
  };

  const commitPush = () => {
    if (!redirectPushCache) {
      return;
    }
    setNavigationStack(redirectPushCache);
    setRedirectPushCache([]);
  };

  const popStack = () => {
    if (!navigationStack) {
      return undefined;
    }
    const newStack = navigationStack;
    const item = newStack.pop();
    setNavigationStack(newStack ?? []);
    return item;
  };

  const popNavigate = (forceDefaultNavigation = false) => {
    const { target, parkingZoneUid, parkingSpaceUid, pmcId } = popStack() || {};
    if (!target) {
      // When completing/skipping onboarding on web we always need to leave the
      // (always-mounted) onboarding modal, regardless of the current route.
      if (forceDefaultNavigation) {
        navigateToAccountLogin();
        return;
      }
      // Only navigate to account if we're actually in a login flow that needs redirection
      // Don't navigate if user is already on another screen and this is just a data update
      try {
        const currentRoute = getState()?.routes?.[getState()?.index ?? 0];
        const routeName = currentRoute?.name;

        // Only navigate to account if we're on a login-related screen
        if (routeName === 'loginSuccess' || routeName === 'onboardingModal') {
          navigateToAccountLogin();
        }
      } catch (error) {
        // If we can't determine the current route, it's safer to navigate to account
        // This maintains backward compatibility while being conservative
        console.log(
          'Could not determine current route, defaulting to account navigation:',
          error
        );
        navigateToAccountLogin();
      }
      return;
    }
    switch (target) {
      case 'BuyShortTermParking':
        navigateToBuyShortTermParking(parkingZoneUid);
        break;
      case 'BuyPermit':
        if (!pmcId) {
          throw new Error('no pmcId set for redirect');
        }
        navigateToBuyPermit(pmcId, parkingSpaceUid);
        break;
      case 'Onboarding':
        navigateToOnboarding();
        break;
      case 'Account':
        navigateToAccountLogin();
        break;
      case 'Services':
        navigateToServices();
        break;
      case 'MainPage':
        navigateToParkScreen();
    }
  };

  const navigateToParkScreen = () => {
    navigateRoot('mainTabs', {
      screen: 'parkScreen',
      params: { screen: 'park' },
    });
  };

  const navigateToAccountLogin = () => {
    navigateAccount('mainTabs', {
      screen: 'accountScreen',
      params: { screen: 'accountMain' },
    });
  };

  const navigateToServices = () => {
    navigateRoot('mainTabs', {
      screen: 'servicesScreen',
      params: {
        screen: 'servicesMain',
      },
    });
  };

  const navigateToBuyShortTermParking = (parkingZoneUid = '') => {
    navigateMain('mainTabs', {
      screen: 'parkScreen',
      params: { screen: 'parkDetail', params: { parkingZoneUid } },
    });
  };

  const navigateToBuyPermit = (_pmcId: string, _parkingSpace?: string) => {
    navigateRoot('longTermParking', {
      screen: 'mapScreen',
      // TODO go to buyScreen with params
      // params: { pmcId, parkingSpace }, //
      // or make login modal and therefore the navigation obsolete
    });
  };

  const navigateToOnboarding = () => {
    navigateAccount('onboardingModal', {
      screen:
        (paymentCards || []).length > 0 ? 'onboardingContinue' : 'welcome',
    });
  };

  const navigateToOnboardingPushNotificationScreen = () => {
    navigateAccount('onboardingModal', {
      screen: 'pushNotifications',
    });
  };

  const navigateToAuthentication = async (fromWebParkZoneDetails = false) => {
    if (fromWebParkZoneDetails) {
      // In web, the Azure redirect does not work for dynamic url, so we need to navigate to the park screen
      navigateToParkScreen();
      // Wait some seconds
      setTimeout(async () => {
        await AuthManager.suSiAsync();
      }, 500);
    } else {
      await AuthManager.suSiAsync();
    }
  };

  const setReturnRedirectToBuyPermit = (
    pmcId?: string,
    parkingSpaceUid?: string,
    allowOnboarding = true
  ) => {
    pushStack(
      {
        target: 'BuyPermit',
        parkingSpaceUid,
        pmcId,
      },
      false
    );

    if (allowOnboarding && !isOnboardingCompleted) {
      pushStack(
        {
          target: 'Onboarding',
        },
        false
      );
    }
    commitPush();
  };

  const setReturnRedirectToBuyShortTermParking = (
    parkingZoneUid: string,
    allowOnboarding = true
  ) => {
    pushStack(
      {
        target: 'BuyShortTermParking',
        parkingZoneUid: parkingZoneUid,
      },
      false
    );

    if (allowOnboarding && !isOnboardingCompleted) {
      pushStack(
        {
          target: 'Onboarding',
        },
        false
      );
    }
    commitPush();
  };

  const setReturnRedirectToAccount = (allowOnboarding = true) => {
    pushStack(
      {
        target: 'Account',
      },
      false
    );
    if (allowOnboarding && !isOnboardingCompleted) {
      pushStack(
        {
          target: 'Onboarding',
        },
        false
      );
    }
    commitPush();
  };

  const setReturnToMainPage = (allowOnboarding = true) => {
    pushStack(
      {
        target: 'MainPage',
      },
      false
    );
    if (allowOnboarding && !isOnboardingCompleted) {
      pushStack(
        {
          target: 'Onboarding',
        },
        false
      );
    }
    commitPush();
  };

  const setReturnRedirectToServices = (allowOnboarding = true) => {
    pushStack(
      {
        target: 'Services',
      },
      false
    );
    if (allowOnboarding && !isOnboardingCompleted) {
      pushStack(
        {
          target: 'Onboarding',
        },
        false
      );
    }
    commitPush();
  };

  const skipOnboarding = async () => {
    const notificationsEnabled = await checkNotificationsPermissions();
    if (notificationsEnabled) {
      setOnboardingCompleted();
    } else {
      navigateToOnboardingPushNotificationScreen();
    }
  };

  const setOnboardingCompleted = () => {
    onboardingCompleted(true);
    useUiStore.getState().setIsSignUpFlow(false);
    if (isWeb) {
      popNavigate(true);
    }
  };

  const clearRedirectStack = () => {
    setNavigationStack([]);
    setRedirectPushCache([]);
  };

  const peekNextNavigationTarget = () => {
    return navigationStack.length > 0
      ? navigationStack[navigationStack.length - 1]
      : undefined;
  };

  return {
    setReturnRedirectToBuyShortTermParking,
    setReturnRedirectToBuyPermit,
    setReturnRedirectToAccount,
    setReturnRedirectToServices,
    setReturnToMainPage,
    setOnboardingCompleted,
    skipOnboarding,
    navigateToNext: popNavigate,
    navigateToAccountLogin,
    navigateToAuthentication,
    navigateToOnboarding,
    navigateToParkScreen,
    clearRedirectStack,
    isInRedirectProcess: () => navigationStack.length > 0,
    currentStack: navigationStack,
    peekNextNavigationTarget,
  };
};
export default useAfterAuthenticationRedirect;
